import { useTranslations } from '@vocab/react';
import { Autosuggest, Badge } from 'braid-design-system';
import { type LegacyRef, useRef, type ComponentProps } from 'react';

import { WorkArrangement } from 'src/graphql/generated';

import translations from './.vocab';

const WorkplaceAutosuggest = Autosuggest<WorkArrangement>;
type AutosuggestProps = ComponentProps<typeof WorkplaceAutosuggest>;
type AutosuggestValue = AutosuggestProps['value'];

interface WorkplaceProps {
  value: WorkArrangement | null;
  setValue: (value?: WorkArrangement) => void;
  errorMessage: string | null;
}

export const WorkplaceView = ({
  value,
  setValue,
  errorMessage,
}: WorkplaceProps) => {
  const workplaceRef = useRef<typeof Autosuggest<WorkArrangement>>(null);

  const { t } = useTranslations(translations);
  const options: Array<AutosuggestValue> = [
    {
      text: t('on-site-label'),
      description: t('on-site-description'),
      value: WorkArrangement.ONSITE,
    },
    {
      text: t('hybrid-label'),
      description: t('hybrid-description'),
      value: WorkArrangement.HYBRID,
    },
    {
      text: t('remote-label'),
      description: t('remote-description'),
      value: WorkArrangement.REMOTE,
    },
  ];

  const firstOption = options.find((option) => option?.value === value) ?? {
    text: '',
  };

  return (
    <Autosuggest
      ref={workplaceRef as unknown as LegacyRef<HTMLInputElement>}
      id="workplace-options-list"
      label={
        <>
          {t('label')} <Badge tone="positive">{t('new-badge')}</Badge>
        </>
      }
      value={firstOption}
      onChange={(val) => {
        setValue(val.value);

        /* If the user misclicks the wrong option, and so immediately clicks on the
         * Autosuggest again, then the options are not redisplayed. This is
         * because the Autosuggest will only pop open again after it has lost
         * focus. We are manually blurring the element to work around this. */
        (workplaceRef?.current as unknown as HTMLElement)?.blur();
      }}
      suggestions={options}
      placeholder={t('placeholder')}
      reserveMessageSpace={true}
      message={errorMessage}
      tone={Boolean(errorMessage) ? 'critical' : 'neutral'}
    />
  );
};
